import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';

// Shares the Copilot open-state between its launcher (the header help-icon slot,
// in AppBar) and the drawer (CopilotWidget), which live in different parts of
// the tree. It ALSO holds the guided-walkthrough state so the overlay
// (CopilotWalkthroughOverlay) — a sibling of the drawer — can drive an
// on-screen tour that survives the drawer closing. The default value makes
// useCopilot() safe outside a provider (e.g. the patient AppBar) — it just
// reports disabled and no-ops.
const CopilotContext = createContext({
  // Is a CopilotProvider actually mounted above us? Distinct from `enabled`,
  // which is the deployment's Copilot flag. Only the provider hosts the
  // walkthrough state and the overlay, so a launcher outside one (the patient
  // AppBar) would be a button that silently does nothing — this is how such a
  // launcher knows not to render at all.
  available: false,
  enabled: false,
  aiEnabled: false,
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
  // Guided walkthrough (read-only). `activeWalkthrough` is the validated,
  // non-PHI definition { title, screenId, steps:[{anchor,message,advanceOn}] }
  // embedded in the /va/message response; `highlightAnchor` is a one-shot
  // spotlight on a single anchor id.
  activeWalkthrough: null,
  highlightAnchor: null,
  startWalkthrough: () => {},
  stopWalkthrough: () => {},
  highlight: () => {},
  clearHighlight: () => {},
  // TGCLOUD-1670 — a request to apply a data overlay to the pedigree the
  // clinician has open. Held here, not dispatched as a window event, because the
  // workspace can have several pedigrees mounted and only the ACTIVE one may
  // respond; PedigreeChart consumes this and checks its own instance first.
  pendingPedigreeOverlay: null,
  showPedigreeOverlay: () => {},
  consumePedigreeOverlay: () => null
});

export function CopilotProvider({ children }) {
  const flags = useVAFeatureFlags();
  const [isOpen, setIsOpen] = useState(false);
  const [activeWalkthrough, setActiveWalkthrough] = useState(null);
  const [highlightAnchor, setHighlightAnchor] = useState(null);
  const [pendingPedigreeOverlay, setPendingPedigreeOverlay] = useState(null);
  // The state drives the consumer's effect; the ref is what the consumer
  // actually reads. Reading inside a setState updater would be an impure read
  // of a value React is free to compute later, which is how a request gets
  // applied twice or not at all.
  const pendingOverlayRef = useRef(null);
  // The Copilot itself is no longer gated — it can answer from the guided
  // walkthrough catalogue with no external call, so there is nothing to switch
  // off. `aiEnabled` is the flag that actually varies: with it false there are no
  // free-text AI answers and no dictation, but walkthroughs still run.
  const enabled = true;
  const aiEnabled = !!(flags && flags.copilotAiEnabled);

  const value = useMemo(
    () => ({
      available: true,
      enabled,
      aiEnabled,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((v) => !v),
      activeWalkthrough,
      highlightAnchor,
      // Starting one clears the other so only a single overlay is ever active.
      // It also closes the chat drawer (D28) — the pedigree sidebar controls the
      // tour points at share the right edge with the drawer, so the drawer would
      // otherwise cover them. The tour lives in its own state, so it persists;
      // the header launcher reopens the chat anytime.
      startWalkthrough: (walkthrough) => {
        setHighlightAnchor(null);
        setActiveWalkthrough(walkthrough || null);
        if (walkthrough) setIsOpen(false);
      },
      stopWalkthrough: () => setActiveWalkthrough(null),
      highlight: (anchor) => {
        setActiveWalkthrough(null);
        setHighlightAnchor(anchor || null);
        if (anchor) setIsOpen(false);
      },
      clearHighlight: () => setHighlightAnchor(null),
      pendingPedigreeOverlay,
      // Unlike a tour or a highlight, applying an overlay does NOT close the
      // drawer. A tour and a spotlight both point at controls on the right edge
      // that the drawer would cover, so they have to get out of the way. An
      // overlay marks the chart itself, which is fully visible beside the open
      // drawer, and leaving it open is what lets the clinician ask the obvious
      // follow-up without reopening anything.
      showPedigreeOverlay: (request) => {
        pendingOverlayRef.current = request || null;
        setPendingPedigreeOverlay(request || null);
      },
      // Read-and-clear, so a request is applied exactly once and a later
      // re-render of the consuming pedigree cannot re-apply it.
      consumePedigreeOverlay: () => {
        const taken = pendingOverlayRef.current;
        pendingOverlayRef.current = null;
        setPendingPedigreeOverlay(null);
        return taken;
      }
    }),
    [
      enabled,
      aiEnabled,
      isOpen,
      activeWalkthrough,
      highlightAnchor,
      pendingPedigreeOverlay
    ]
  );

  return (
    <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
  );
}

export function useCopilot() {
  return useContext(CopilotContext);
}
